import type { BloodTestAbnormality, BloodTestItem, BloodTestResult } from '../api/bloodTest';
import { computeAbnormalityFromReference } from './bloodTestReference';

/** 編集フィールド用の文字列（例: 8600L, 12.5, <0.02H） */
export function formatItemForEdit(item: BloodTestItem): string {
  const flag = item.abnormality === 'L' || item.abnormality === 'H' ? item.abnormality : '';
  if (item.value !== null) {
    const numStr =
      Number.isInteger(item.value) ? String(item.value) : String(parseFloat(item.value.toFixed(3)));
    return `${numStr}${flag}`;
  }
  if (item.raw_text?.trim()) {
    return `${item.raw_text.trim()}${flag}`;
  }
  return '';
}

/** 編集文字列を value / raw_text / abnormality に分解する */
export function parseItemEditInput(input: string): Pick<
  BloodTestItem,
  'value' | 'raw_text' | 'abnormality'
> {
  const trimmed = input.trim();
  if (!trimmed) {
    return { value: null, raw_text: null, abnormality: null };
  }

  let abnormality: BloodTestAbnormality = null;
  let core = trimmed;
  const flagMatch = core.match(/^(.+)([LH])$/);
  if (flagMatch) {
    abnormality = flagMatch[2] as 'L' | 'H';
    core = flagMatch[1].trim();
  }

  if (!core) {
    return { value: null, raw_text: null, abnormality };
  }

  const normalized = core.replace(/,/g, '');
  if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    return { value: parseFloat(normalized), raw_text: null, abnormality };
  }

  return { value: null, raw_text: core, abnormality };
}

/** 基準値に基づく L/H を優先して項目を正規化 */
function withReferenceAbnormality(item: BloodTestItem): BloodTestItem {
  const abnormality = computeAbnormalityFromReference(item.key, item.value);
  return { ...item, abnormality };
}

/** 解析結果から各項目の編集用文字列マップを生成 */
export function createItemValueEdits(items: BloodTestItem[]): Record<string, string> {
  return Object.fromEntries(
    items.map((item) => [item.key, formatItemForEdit(withReferenceAbnormality(item))])
  );
}

/** 編集内容を反映したコピー用の結果オブジェクトを組み立てる */
export function buildResultFromEdits(
  result: BloodTestResult,
  itemValueEdits: Record<string, string>
): BloodTestResult {
  return {
    ...result,
    items: result.items.map((item) => {
      const parsed = parseItemEditInput(itemValueEdits[item.key] ?? formatItemForEdit(item));
      const abnormality = computeAbnormalityFromReference(item.key, parsed.value);
      return {
        ...item,
        ...parsed,
        abnormality,
      };
    }),
  };
}
